import { FC } from 'react'
import { SolveMetricRunTableRow } from '../../../api/solve-metrics-API'
import { SectionEmpty } from './Section'

export type SolverStatisticsProps = {
  baseline?: SolveMetricRunTableRow | null
  proposed?: SolveMetricRunTableRow | null
}

const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const fmtHours = (v: number | null): string => (v === null ? 'N/A' : v.toFixed(2))

// Mesh time is not reported directly; derive it as wall - solve (per source report).
const meshTime = (row?: SolveMetricRunTableRow | null): number | null => {
  const wall = toNum(row?.wallTimeH)
  const solve = toNum(row?.solveTimeH)
  if (wall === null || solve === null) return null
  return wall - solve
}

const changeCell = (
  base: number | null,
  prop: number | null,
  unit = 'h'
): { text: string; cls: string } => {
  if (base === null || prop === null) return { text: 'N/A', cls: '' }
  const diff = prop - base
  if (diff === 0) return { text: '0', cls: '' }
  const pct = base !== 0 ? ` (${diff > 0 ? '+' : ''}${((diff / base) * 100).toFixed(1)}%)` : ''
  return {
    text: `${diff > 0 ? '+' : ''}${diff.toFixed(2)} ${unit}${pct}`,
    cls: diff > 0 ? 'text-red-600' : 'text-green-700'
  }
}

/** Solver statistics comparison (CFD report §4). */
const SolverStatistics: FC<SolverStatisticsProps> = ({ baseline, proposed }) => {
  if (!baseline && !proposed) return <SectionEmpty />

  const rows: {
    label: string
    base: string
    prop: string
    change?: { text: string; cls: string }
  }[] = [
    { label: 'Run', base: baseline?.run ?? '', prop: proposed?.run ?? '' },
    { label: 'Start date', base: baseline?.startTime ?? '', prop: proposed?.startTime ?? '' },
    {
      label: 'Solve time (h)',
      base: fmtHours(toNum(baseline?.solveTimeH)),
      prop: fmtHours(toNum(proposed?.solveTimeH)),
      change: changeCell(toNum(baseline?.solveTimeH), toNum(proposed?.solveTimeH))
    },
    {
      label: 'Wall time (h)',
      base: fmtHours(toNum(baseline?.wallTimeH)),
      prop: fmtHours(toNum(proposed?.wallTimeH)),
      change: changeCell(toNum(baseline?.wallTimeH), toNum(proposed?.wallTimeH))
    },
    {
      label: 'Mesh time (h)',
      base: fmtHours(meshTime(baseline)),
      prop: fmtHours(meshTime(proposed)),
      change: changeCell(meshTime(baseline), meshTime(proposed))
    },
    {
      label: 'Cores used',
      base: toNum(baseline?.sumProcessors)?.toString() ?? '',
      prop: toNum(proposed?.sumProcessors)?.toString() ?? '',
      change: changeCell(toNum(baseline?.sumProcessors), toNum(proposed?.sumProcessors), '')
    },
    { label: 'CPU core type', base: baseline?.coreType ?? '', prop: proposed?.coreType ?? '' },
    {
      label: 'Solver software',
      base: baseline?.solutionSoftware ?? '',
      prop: proposed?.solutionSoftware ?? ''
    }
  ]

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-neutral-70">
            <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
              Metric
            </th>
            <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
              Baseline
            </th>
            <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
              Proposed
            </th>
            <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-right font-bold">
              Change
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.label}>
              <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1">
                {row.label}
              </td>
              <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1">
                {row.base}
              </td>
              <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1">
                {row.prop}
              </td>
              <td
                className={`border border-gray-200 dark:border-neutral-60 px-2 py-1 text-right tabular-nums ${
                  row.change?.cls ?? ''
                }`}>
                {row.change?.text ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default SolverStatistics
